import { TruckingCompanyTypes, ServiceRequestTypes } from './enums';

import { apiOptions } from './apiOptions';
import { isGuid } from '../utils';

const appDomain = apiOptions.appDomain;
const messageBrokerWebSocket = apiOptions.messageBrokerWebSocket;

const getCurrentMomentumSubUrl = (skipAmsinsSubRoute: boolean) => {
  const fullUrl = window.location.href;

  if (fullUrl) {
    const urlParts = fullUrl.split('/AMSINS');
    if (urlParts && urlParts.length === 2) {
      return skipAmsinsSubRoute ? urlParts[1] : 'AMSINS' + urlParts[1];
    }
  }

  return null;
};

export const siteUrls = {
  // tasks
  getTaskCreateUrl: (isMomentumEnabled: boolean, noteId: string, insuredId: string, policyIds: string[], isBlankPage: boolean = false, fileId: string) => {
    const insuredParam = isGuid(insuredId) ? `&InsuredId=${insuredId}` : '';

    const noteIdParam = noteId && isGuid(noteId) ? `&NoteId=${noteId}` : '';

    if (isMomentumEnabled) {
      let policyIdsParam = '';

      if (policyIds && policyIds.length) {
        policyIdsParam = `&${policyIds.map((x) => `PolicyIds=${x}`).join('&')}`;
      }

      const fileIdParam = fileId && isGuid(fileId) ? `&FileId=${fileId}` : '';

      const redirectUrl = getCurrentMomentumSubUrl(true);
      const returnUrlParam = redirectUrl ? `&MomentumUrl=${redirectUrl}` : '';

      const url = `AMSINS/Tasks/Insert?${noteIdParam}${policyIdsParam}${insuredParam}${fileIdParam}${returnUrlParam}`;

      if (isBlankPage) {
        return appDomain + url;
      } else {
        return `/` + url;
      }
    }

    return `${appDomain}Tracker_ProjectTickets/Insert.aspx?${noteIdParam}${insuredParam}`;
  },

  getTaskEditUrl: (isMomentumEnabled: boolean, id: string) => {
    const returnSubUrl = getCurrentMomentumSubUrl(true);

    if (isMomentumEnabled) {
      return `/AMSINS/Tasks/Edit/${id}` + (returnSubUrl ? `?MomentumUrl=${returnSubUrl}` : '');
    }

    return `${appDomain}Tracker_ProjectTickets/Edit.aspx?Id=${id}` + (returnSubUrl ? `&Return=AMSINS${returnSubUrl}` : '');
  },

  getTaskDetailsUrl: (isMomentumEnabled: boolean, taskId: string) => {
    if (isMomentumEnabled) {
      return `/AMSINS/Tasks/Details/${taskId}/Information`;
    }

    return `${appDomain}Tracker_ProjectTickets/Details.aspx?Id=${taskId}`;
  },

  // policies
  policyDetailsUrl: `${appDomain}Policies/Details.aspx`,

  getPolicyDetailsUrl: (isMomentumEnabled: boolean, policyId: string, isQuote: boolean) => {
    if (isMomentumEnabled) {
      if (isQuote) {
        return `/AMSINS/Quotes/Details/${policyId}/Information`;
      }

      return `/AMSINS/Policies/Details/${policyId}/Information`;
    }

    return `${appDomain}Policies/Details.aspx?Id=${policyId}`;
  },

  // claims
  getClaimDetailsUrl: (isMomentumEnabled: boolean, claimId: string) => {
    if (isMomentumEnabled) {
      return `/AMSINS/Claims/Details/${claimId}/Information`;
    }

    return `${appDomain}Claims/Details.aspx?Id=${claimId}`;
  },

  // service requests

  getServiceRequestCreateUrl: (isMomentumEnabled: boolean, noteId: string, insuredId: string, fileId: string, isBlankPage = false) => {
    const insuredParam = isGuid(insuredId) ? `&InsuredId=${insuredId}` : '';

    const noteIdParam = noteId && isGuid(noteId) ? `&NoteId=${noteId}` : '';

    if (isMomentumEnabled) {
      const redirectUrl = getCurrentMomentumSubUrl(true);
      const returnUrlParam = redirectUrl ? `&MomentumUrl=${redirectUrl}` : '';

      const fileIdParam = fileId && isGuid(fileId) ? `&FileId=${fileId}` : '';

      const url = `AMSINS/ServiceRequests/Generic/Insert?${noteIdParam}${insuredParam}${fileIdParam}${returnUrlParam}`;

      if (isBlankPage) {
        return appDomain + url;
      } else {
        return `/` + url;
      }
    }

    return `${appDomain}Requests/Insert.aspx?${noteIdParam}${insuredParam}`;
  },

  getServiceRequestEditUrl: (isMomentumEnabled: boolean, serviceRequestType: ServiceRequestTypes, srId: string) => {
    //new pipeline pages always with amsins sub route
    const returnSubUrl = getCurrentMomentumSubUrl(true);

    if (isMomentumEnabled) {
      switch (serviceRequestType) {
        case ServiceRequestTypes.Generic:
          return `/AMSINS/ServiceRequests/Generic/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.PolicyChangeRequestOther:
          return `/AMSINS/ServiceRequests/PolicyChange/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.AddDriver:
          return `/AMSINS/ServiceRequests/AddDriver/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.ReplaceDriver:
          return `/AMSINS/ServiceRequests/ReplaceDriver/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.RemoveDriver:
          return `/AMSINS/ServiceRequests/RemoveDriver/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.AddressChange:
          return `/AMSINS/ServiceRequests/AddressChange/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.VehicleTransfer:
          return `/AMSINS/ServiceRequests/VehicleTransfer/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.CertificateOfInsurance:
          return `/AMSINS/ServiceRequests/CertificateOfInsurance/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.GeneralNew:
          return `/AMSINS/ServiceRequests/GeneralNew/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.AddVehicle:
          return `/AMSINS/ServiceRequests/AddVehicle/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.ReplaceVehicle:
          return `/AMSINS/ServiceRequests/ReplaceVehicle/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
      }
      // redirect to MomentumUrl should be fixed here :
      // https://nowcerts.atlassian.net/browse/NOWCERTS-5453
    }

    const returnSubUrlParam = returnSubUrl ? '&Return=AMSINS' + returnSubUrl : '';

    switch (serviceRequestType) {
      case ServiceRequestTypes.Generic:
        return `${appDomain}Requests/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.PolicyChangeRequestOther:
        return `${appDomain}Requests/Edit.aspx?Type=1&Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.AddDriver:
        return `${appDomain}/ServiceRequests_AddDrivers/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.ReplaceDriver:
        return `${appDomain}ServiceRequests_ReplaceDrivers/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.RemoveDriver:
        return `${appDomain}ServiceRequests_RemoveDrivers/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.AddressChange:
        return `${appDomain}ServiceRequests_AddressChanges/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.VehicleTransfer:
        return `${appDomain}ServiceRequests_VehicleTransfers/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.CertificateOfInsurance:
        return `${appDomain}ServiceRequests_CertificateofInsurances/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.GeneralNew:
        return `${appDomain}ServiceRequests_GeneralNews/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.AddVehicle:
        return `${appDomain}ServiceRequests_AddVehicles/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.ReplaceVehicle:
        return `/AMSINS/ServiceRequests/ReplaceVehicle/Edit/${srId}${returnSubUrl ? '?MomentumUrl=' + returnSubUrl : ''}`; // not found old page for this new type
    }
  },

  getServiceRequestDetailsUrl: (isMomentumEnabled: boolean, serviceRequestType: ServiceRequestTypes, srId: string) => {
    //new pipeline pages always with amsins sub route
    const returnSubUrl = getCurrentMomentumSubUrl(true);

    if (isMomentumEnabled) {
      switch (serviceRequestType) {
        case ServiceRequestTypes.AddDriver:
          return `/AMSINS/ServiceRequests/Driver/Details/${srId}/Information`;
        case ServiceRequestTypes.AddVehicle:
          return `/AMSINS/ServiceRequests/Vehicle/Details/${srId}/Information`;
        case ServiceRequestTypes.Generic:
        case ServiceRequestTypes.PolicyChangeRequestOther:
          return `/AMSINS/ServiceRequests/Details/${srId}/Information`;
        case ServiceRequestTypes.ReplaceDriver:
          return `/AMSINS/ServiceRequests/ReplaceDriver/Details/${srId}/Information`;
        case ServiceRequestTypes.RemoveDriver:
          return `/AMSINS/ServiceRequests/RemoveDriver/Details/${srId}/Information`;
        case ServiceRequestTypes.AddressChange:
          return `/AMSINS/ServiceRequests/AddressChange/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.VehicleTransfer:
          return `/AMSINS/ServiceRequests/VehicleTransfer/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.CertificateOfInsurance:
          return `/AMSINS/ServiceRequests/CertificateOfInsurance/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.GeneralNew:
          return `/AMSINS/ServiceRequests/GeneralNew/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
        case ServiceRequestTypes.ReplaceVehicle:
          return `/AMSINS/ServiceRequests/ReplaceVehicle/Edit/${srId}?MomentumUrl=${returnSubUrl}`;
      }
    }

    const returnSubUrlParam = returnSubUrl ? '&Return=AMSINS' + returnSubUrl : '';

    switch (serviceRequestType) {
      case ServiceRequestTypes.Generic:
      case ServiceRequestTypes.AddDriver:
      case ServiceRequestTypes.PolicyChangeRequestOther:
      case ServiceRequestTypes.GeneralNew:
      case ServiceRequestTypes.AddVehicle:
        return `${appDomain}Requests/Details.aspx?Id=${srId}`;
      case ServiceRequestTypes.ReplaceDriver:
        return `${appDomain}ServiceRequests_ReplaceDrivers/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.RemoveDriver:
        return `${appDomain}ServiceRequests_RemoveDrivers/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.AddressChange:
        return `${appDomain}ServiceRequests_AddressChanges/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.VehicleTransfer:
        return `${appDomain}ServiceRequests_VehicleTransfers/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.CertificateOfInsurance:
        return `${appDomain}ServiceRequests_CertificateofInsurances/Edit.aspx?Id=${srId}${returnSubUrlParam}`;
      case ServiceRequestTypes.ReplaceVehicle:
        return `/AMSINS/ServiceRequests/ReplaceVehicle/Edit/${srId}${returnSubUrl ? '?MomentumUrl=' + returnSubUrl : ''}`; // not found old page for this new type
    }
  },

  // trucking company

  getTruckingCompanyDetailsUrl: (isMomentumEnabled: boolean, truckingCompanyId: string, truckingCompanyType: TruckingCompanyTypes) => {
    if (isMomentumEnabled) {
      switch (truckingCompanyType) {
        case TruckingCompanyTypes.Insured:
          return `/AMSINS/Insureds/Details/${truckingCompanyId}/Information`;
        case TruckingCompanyTypes.Prospect:
          return `/AMSINS/Prospects/Details/${truckingCompanyId}/Information`;
        case TruckingCompanyTypes.MGA:
          return `/AMSINS/MGA/Details/${truckingCompanyId}/Information`;
        case TruckingCompanyTypes.Carrier:
          return `/AMSINS/Carriers/Details/${truckingCompanyId}/Information`;
        case TruckingCompanyTypes.FinanceCompany:
          return `/AMSINS/FinanceCompanies/Details/${truckingCompanyId}/Information`;
        case TruckingCompanyTypes.ReferralSource:
          return `/AMSINS/ReferralSources/Details/${truckingCompanyId}/Information`;
        case TruckingCompanyTypes.Other:
          return `/AMSINS/OtherCompanies/Details/${truckingCompanyId}/Information`;
        default:
          return `/AMSINS/Insureds/Details/${truckingCompanyId}/Information`;
      }
    }

    return `${appDomain}TruckingCompanies/Details.aspx?Id=${truckingCompanyId}`;
  },

  // todo list

  getCreateToDoUrl: (isMomentumEnabled: boolean, noteId: string, insuredId: string) => {
    const insuredParam = isGuid(insuredId) ? `&InsuredId=${insuredId}` : '';

    if (isMomentumEnabled) {
      const redirectUrl = getCurrentMomentumSubUrl(true);
      const returnUrlParam = redirectUrl ? `&MomentumUrl=${redirectUrl}` : '';

      return `/AMSINS/Todos/List?NoteId=${noteId}${insuredParam}${returnUrlParam}`;
    }

    return `${appDomain}Pages/ToDoList.aspx?NoteId=${noteId}${insuredParam}`;
  },

  getCreateOpportunityUrl: (isMomentumEnabled: boolean) => {
    const redirectUrl = getCurrentMomentumSubUrl(isMomentumEnabled);
    //new pipeline pages always with amsins sub route

    if (isMomentumEnabled) {
      const returnUrlParam = redirectUrl ? `&MomentumUrl=${redirectUrl}` : '';
      return `/AMSINS/Opportunities/Insert?${returnUrlParam}`;
    }

    const returnUrlParam = redirectUrl ? `&Return=${redirectUrl}` : '';
    return `${appDomain}TruckingCompanyOpportunities/Insert.aspx?${returnUrlParam}`;
  },

  getEditOpportunityUrl: (isMomentumEnabled: boolean, id: string) => {
    const redirectUrl = getCurrentMomentumSubUrl(isMomentumEnabled);

    if (!isGuid(id)) return null;

    if (isMomentumEnabled) {
      const returnUrlParam = redirectUrl ? `?MomentumUrl=${redirectUrl}` : '';

      return `/AMSINS/Opportunities/Edit/${id}${returnUrlParam}`;
    }

    const returnUrlParam = redirectUrl ? `&Return=${redirectUrl}` : '';

    return `${appDomain}TruckingCompanyOpportunities/Edit.aspx?${'&Id=' + id}${returnUrlParam}`;
  },

  getOpportunityDetailsUrl: (isMomentumEnabled: boolean, agentId: string) => {
    if (isMomentumEnabled) {
      return `/AMSINS/Opportunities/Details/${agentId}/Information`;
    }

    return `${appDomain}TruckingCompanyOpportunities/Details.aspx?Id=${agentId}`;
  },

  getOpportunitiesListUrl: (isMomentumEnabled: boolean) => {
    if (isMomentumEnabled) {
      return `${appDomain}AMSINS/Opportunities/List`;
    }

    return `${appDomain}TruckingCompanyOpportunities/List.aspx?Type=0`;
  },

  // agents
  agentDetailsUrl: `${appDomain}Agents/Details.aspx`,

  getAgentDetailsUrl: (isMomentumEnabled: boolean, agentId: string) => {
    if (isMomentumEnabled) {
      return `/AMSINS/Agents/Details/${agentId}/Information`;
    }

    return `${appDomain}Agents/Details.aspx?Id=${agentId}`;
  },

  sentCerticatesList: (isMomentumEnabled: boolean, truckingCompanyId: string) => {
    if (isMomentumEnabled) {
      return `/AMSINS/Insureds/Details/${truckingCompanyId}/CertificatesSent`;
    }

    return `${appDomain}TruckingCompanies/Details.aspx?Id=${truckingCompanyId}&Page=5`;
  },

  //system messages
  systemMessagesMomentum: `${appDomain}AMSINS/SystemMessages/Details/`,

  //WebSocket
  messageBrokerWebSocket: messageBrokerWebSocket,
};
